import React, { useState, useMemo } from 'react';
import { DialogActions, Box } from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { usersServices } from '@services';
import { ButtonV2 } from 'components/common/ui';
import { FieldV2 } from 'components/common/form-controls';
import { LANGUAGES } from '@constants';
import { CUSTOMER_VEHICLE_USER_ROLES } from '../constants';

import validationSchema from './validationSchema';
import styles from './addCustomerVehicleUser.style';

const CustomerVehicleUserForm = ({
  userId,
  defaultValues = {},
  onSubmit,
  onCancel,
  onRemove,
}) => {
  const [userExists, setUserExists] = useState(!!userId);

  const resolver = useMemo(
    () => yupResolver(validationSchema(userExists)),
    [userExists]
  );

  const methods = useForm({
    resolver,
    mode: 'onChange',
    defaultValues,
  });

  const { handleSubmit, formState, getValues, reset } = methods;
  const roles = Object.keys(CUSTOMER_VEHICLE_USER_ROLES);

  const rolesOptions = roles.map((key) => CUSTOMER_VEHICLE_USER_ROLES[key]);

  const handleEmailBlur = async () => {
    if (userExists) {
      return;
    }
    try {
      const { email } = getValues();

      if (email) {
        setUserExists(false);

        const { data } = await usersServices.getUserByEmail(email);

        if (data) {
          reset({
            email,
            firstName: data.firstName,
            lastName: data.lastName,
            phoneNumber: data.phoneNumber,
            lang: data.lang,
          });
          setUserExists(true);
        }
      }
    } catch (e) {
      console.warn(e); // eslint-disable-line no-console
      setUserExists(false);
    }
  };

  return (
    <FormProvider key={`customer-vehicle-user-form-${userExists}`} {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={styles.fieldRow}>
          <FieldV2
            isRequired={!userExists}
            name="email"
            label="Email"
            disabled={userExists}
            control={methods.control}
            onBlur={handleEmailBlur}
          />
          <FieldV2
            isRequired={!userExists}
            name="phoneNumber"
            label="Phone Number"
            disabled={userExists}
            control={methods.control}
          />
        </Box>
        <Box sx={styles.fieldRow}>
          <FieldV2
            isRequired={!userExists}
            name="firstName"
            label="First Name"
            disabled={userExists}
            control={methods.control}
          />
          <FieldV2
            isRequired={!userExists}
            name="lastName"
            label="Last Name"
            disabled={userExists}
            control={methods.control}
          />
        </Box>
        <Box sx={styles.fieldRow}>
          <FieldV2
            isRequired
            name="role"
            label="Role"
            type="dropdown"
            options={rolesOptions}
            control={methods.control}
          />
          <FieldV2
            isRequired
            name="lang"
            label="Language"
            type="dropdown"
            options={LANGUAGES}
            control={methods.control}
            disabled={userExists && defaultValues.language}
          />
        </Box>

        <DialogActions>
          <Box sx={styles.footer}>
            {userId && (
              <ButtonV2 variant="secondary" onClick={onRemove}>
                Remove User
              </ButtonV2>
            )}
            <Box sx={styles.buttons}>
              <ButtonV2 variant="secondary" onClick={onCancel}>
                Cancel
              </ButtonV2>
              <ButtonV2 type="submit" loading={formState.isSubmitting}>
                Submit
              </ButtonV2>
            </Box>
          </Box>
        </DialogActions>
      </form>
    </FormProvider>
  );
};

export default CustomerVehicleUserForm;
